// src/pages/AdminFeedbacks.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmtDT = (v) => (v ? new Date(v).toLocaleString() : "-");

export default function AdminFeedbacks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // search + filters
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // pagination (client-side)
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get("/api/admin/feedback", {
        params: { limit: 200 }, // backend already supports this
      });
      setItems(data.items || []);
    } catch (e) {
      console.error("Feedback load error", e);
      setErr(e?.response?.data?.message || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // whenever filters/page-size change, go back to page 1
  useEffect(() => {
    setPage(1);
  }, [q, fromDate, toDate, size]);

  // apply search + date filters on client
  const filtered = useMemo(() => {
    let arr = items;

    if (q.trim()) {
      const term = q.toLowerCase();
      arr = arr.filter((fb) => {
        const name = (fb.name || "").toLowerCase();
        const email = (fb.email || "").toLowerCase();
        const mobile = (fb.mobile || "").toLowerCase();
        const message = (fb.message || "").toLowerCase();
        return (
          name.includes(term) ||
          email.includes(term) ||
          mobile.includes(term) ||
          message.includes(term)
        );
      });
    }

    if (fromDate) {
      const from = new Date(fromDate + "T00:00:00");
      arr = arr.filter((fb) =>
        fb.created_at ? new Date(fb.created_at) >= from : false
      );
    }

    if (toDate) {
      const to = new Date(toDate + "T23:59:59");
      arr = arr.filter((fb) =>
        fb.created_at ? new Date(fb.created_at) <= to : false
      );
    }

    return arr;
  }, [items, q, fromDate, toDate]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const start = (page - 1) * size;
  const current = filtered.slice(start, start + size);

  // -------- EXPORT HANDLERS --------

  const handleExportExcel = () => {
    if (!filtered.length) {
      alert("No feedback to export");
      return;
    }

    const sheetData = filtered.map((fb, idx) => ({
      "Sl No": idx + 1,
      Name: fb.name || "",
      Email: fb.email || "",
      Mobile: fb.mobile || "",
      Message: fb.message || "",
      Created: fmtDT(fb.created_at),
    }));

    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Feedback");
    XLSX.writeFile(wb, "yogapay_feedback.xlsx");
  };

  const handleExportPDF = () => {
    if (!filtered.length) {
      alert("No feedback to export");
      return;
    }

    const doc = new jsPDF("p", "pt", "a4");
    doc.setFontSize(14);
    doc.text("YogaPay Website Feedback", 40, 30);

    const head = [["Sl", "Name", "Email", "Mobile", "Message", "Created"]];

    const body = filtered.map((fb, idx) => [
      idx + 1,
      fb.name || "",
      fb.email || "",
      fb.mobile || "",
      fb.message || "",
      fmtDT(fb.created_at),
    ]);

    autoTable(doc, {
      head,
      body,
      startY: 50,
      styles: { fontSize: 8, cellWidth: "wrap" },
      columnStyles: {
        1: { cellWidth: 90 }, // Name
        2: { cellWidth: 130 }, // Email
        3: { cellWidth: 70 }, // Mobile
        4: { cellWidth: 170 }, // Message
      },
      headStyles: { fillColor: [15, 118, 110] }, // teal header
    });

    doc.save("yogapay_feedback.pdf");
  };

  // ---------------------------------

  return (
    <div style={{ background: "#f3f4f6", minHeight: "100vh" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 22 }}>Feedback</h2>
          <Link
            to="/dashboard"
            style={{
              textDecoration: "none",
              padding: "6px 12px",
              borderRadius: 999,
              border: "1px solid #0f172a",
              background: "#ffffff",
              fontSize: 13,
            }}
          >
            ← Back to Dashboard
          </Link>
        </div>

        {/* Card */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            padding: 12,
            boxShadow: "0 4px 10px rgba(15,23,42,0.06)",
          }}
        >
          {/* Top row: info + refresh + export */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Showing website feedback (max 200). Total filtered:&nbsp;
              <strong>{total}</strong>
            </div>

            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={handleExportExcel}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #15803d",
                  background: "#dcfce7",
                  color: "#166534",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Export Excel
              </button>
              <button
                type="button"
                onClick={handleExportPDF}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #b91c1c",
                  background: "#fee2e2",
                  color: "#b91c1c",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Export PDF
              </button>
              <button
                type="button"
                onClick={load}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #0f766e",
                  background: "#E7F8F1",
                  color: "#0f766e",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Filters row */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 10,
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="Search name / email / mobile / message"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{
                flex: 1,
                minWidth: 220,
                padding: "6px 9px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                fontSize: 13,
              }}
            />
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <label style={{ fontSize: 11, color: "#475569" }}>
                From:&nbsp;
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{
                    padding: "4px 6px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 12,
                  }}
                />
              </label>
              <label style={{ fontSize: 11, color: "#475569" }}>
                To:&nbsp;
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{
                    padding: "4px 6px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 12,
                  }}
                />
              </label>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 12, fontSize: 13 }}>Loading…</div>
          ) : err ? (
            <div style={{ padding: 12, fontSize: 13, color: "#b91c1c" }}>
              {err}
            </div>
          ) : total === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: "#64748b" }}>
              No feedback found for the selected filters.
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      {[
                        "#",
                        "Name",
                        "Email",
                        "Mobile",
                        "Message",
                        "Created",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: 8,
                            borderBottom: "1px solid #e5e7eb",
                            whiteSpace: h === "Message" ? "nowrap" : "normal",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {current.map((fb, idx) => (
                      <tr
                        key={fb.id || `${fb.email}-${start + idx}`}
                        style={{
                          background:
                            (start + idx) % 2 === 0 ? "#ffffff" : "#f8fafc",
                        }}
                      >
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid #e5e7eb",
                            width: 40,
                          }}
                        >
                          {start + idx + 1}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid #e5e7eb",
                            maxWidth: 120,
                            wordBreak: "break-word",
                          }}
                        >
                          {fb.name || "-"}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid #e5e7eb",
                            maxWidth: 180,
                            wordBreak: "break-all",
                          }}
                        >
                          {fb.email}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid #e5e7eb",
                            maxWidth: 110,
                            wordBreak: "break-word",
                          }}
                        >
                          {fb.mobile || "-"}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid #e5e7eb",
                            maxWidth: 260,
                            wordBreak: "break-word",
                          }}
                        >
                          {fb.message}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid #e5e7eb",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fmtDT(fb.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                  fontSize: 12,
                  color: "#475569",
                }}
              >
                <div>
                  Page <strong>{page}</strong> of{" "}
                  <strong>{totalPages}</strong> | Showing{" "}
                  <strong>
                    {current.length} / {total}
                  </strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 999,
                      border: "1px solid #cbd5e1",
                      background: page <= 1 ? "#e5e7eb" : "#ffffff",
                      cursor: page <= 1 ? "default" : "pointer",
                    }}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={page >= totalPages}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 999,
                      border: "1px solid #cbd5e1",
                      background:
                        page >= totalPages ? "#e5e7eb" : "#ffffff",
                      cursor: page >= totalPages ? "default" : "pointer",
                    }}
                  >
                    Next
                  </button>
                  <select
                    value={size}
                    onChange={(e) => setSize(parseInt(e.target.value, 10))}
                    style={{
                      padding: "4px 6px",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      background: "#ffffff",
                    }}
                  >
                    {[10, 20, 50].map((n) => (
                      <option key={n} value={n}>
                        {n} / page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
